/**
 * Consola de platformă (`/admin`) — zona echipei Menuvia, NU a restaurantelor.
 *
 * Reguli:
 *  • gate dublu: UI-ul ascunde, serverul decide (`/api/v1/admin/*` întoarce 404 pentru non-admin);
 *  • zero date hardcodate: planuri, flag-uri și limite vin din API;
 *  • nicio acțiune distructivă fără dialog de confirmare (§38).
 */
import { useEffect, useState } from 'react';
import { NavLink, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Icon,
  InlineAlert,
  StatusDot,
  cx,
  useToast,
  type IconName,
} from '../../components/ui.js';
import { api } from '../../lib/api.js';
import { formatCurrency, formatDateTime, formatNumber, relativeTime, useResource } from '../../lib/hooks.js';
import { errorMessage, useSession } from '../../lib/session.js';
import {
  AdminCard,
  AdminFilterInput,
  AdminHeader,
  AdminLoading,
  AdminRow,
  AdminShell,
  AdminTable,
  AdminTableSkeleton,
  DarkKpi,
  Td,
} from './AdminKit.js';
import { AdminAudit, AdminFlags, AdminPlans, AdminSystemEvents } from './AdminConfig.js';
import { AdminInvoicesPage, AdminSubscriptionsPage } from './AdminBilling.js';
import { AdminOperate } from './AdminOperate.js';

/* ─────────────────────────────── shell ─────────────────────────────── */

const ADMIN_NAV: Array<{ to: string; label: string; icon: IconName; end?: boolean; group?: string }> = [
  { to: '/admin', label: 'Privire generală', icon: 'chart', end: true, group: 'Platformă' },
  { to: '/admin/accounts', label: 'Conturi', icon: 'store', group: 'Platformă' },
  { to: '/admin/restaurants', label: 'Restaurante', icon: 'grid', group: 'Platformă' },
  { to: '/admin/users', label: 'Utilizatori', icon: 'users', group: 'Platformă' },
  { to: '/admin/plans', label: 'Planuri', icon: 'tag', group: 'Platformă' },
  { to: '/admin/subscriptions', label: 'Abonamente', icon: 'card', group: 'Platformă' },
  { to: '/admin/invoices', label: 'Facturi', icon: 'chart', group: 'Platformă' },
  { to: '/admin/flags', label: 'Feature flags', icon: 'sparkles', group: 'Platformă' },
  { to: '/admin/audit', label: 'Audit', icon: 'shield', group: 'Platformă' },
  { to: '/admin/system', label: 'Sistem', icon: 'settings', group: 'Platformă' },
  { to: '/admin/operate/menus', label: 'Meniuri', icon: 'menu', group: 'Restaurant' },
  { to: '/admin/operate/products', label: 'Produse', icon: 'tag', group: 'Restaurant' },
  { to: '/admin/operate/qr', label: 'Coduri QR', icon: 'qr', group: 'Restaurant' },
  { to: '/admin/operate/displays', label: 'Ecrane', icon: 'display', group: 'Restaurant' },
  { to: '/admin/operate/playlists', label: 'Liste de redare', icon: 'image', group: 'Restaurant' },
  { to: '/admin/operate/orders', label: 'Comenzi', icon: 'receipt', group: 'Restaurant' },
  { to: '/admin/operate/promotions', label: 'Promoții', icon: 'sparkles', group: 'Restaurant' },
  { to: '/admin/operate/locations', label: 'Locații', icon: 'pin', group: 'Restaurant' },
  { to: '/admin/operate/team', label: 'Echipă', icon: 'users', group: 'Restaurant' },
  { to: '/admin/operate/analytics', label: 'Statistici', icon: 'chart', group: 'Restaurant' },
];

export function AdminLayout() {
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  if (session.status === 'loading') return <AdminLoading />;

  if (session.status === 'anonymous') {
    return (
      <AdminShell>
        <div className="flex justify-center px-4 py-16">
          <div className="w-full max-w-md">
            <InlineAlert tone="warning" title="Ai nevoie de o sesiune activă">
              Consola de platformă este disponibilă doar utilizatorilor cu rol de administrator Menuvia.
            </InlineAlert>
            <Button className="mt-4 w-full" onClick={() => navigate('/login?next=/admin')}>
              Mergi la autentificare
            </Button>
          </div>
        </div>
      </AdminShell>
    );
  }

  if (!session.user?.isPlatformAdmin) {
    return (
      <AdminShell>
        <div className="flex justify-center px-4 py-16">
          <div className="w-full max-w-md">
            <InlineAlert tone="danger" title="Acces restricționat">
              Contul <span className="font-medium">{session.user?.email}</span> nu are drepturi de administrare a
              platformei. Dacă ai primit acces recent, reautentifică-te pentru a reîmprospăta sesiunea.
            </InlineAlert>
            <Button variant="secondary" className="mt-4 w-full" onClick={() => navigate('/app')}>
              Înapoi la dashboard
            </Button>
          </div>
        </div>
      </AdminShell>
    );
  }

  const operating = location.pathname.startsWith('/admin/operate');

  return (
    <AdminShell>
      <div className={cx('mx-auto flex w-full gap-8 px-4 py-8 sm:px-6', operating ? 'max-w-[1440px]' : 'max-w-7xl')}>
        <nav className="hidden w-56 shrink-0 flex-col gap-1 lg:flex">
          {ADMIN_NAV.map((item, index) => {
            const showGroup = index === 0 || item.group !== ADMIN_NAV[index - 1]?.group;
            return (
              <div key={item.to} className="flex flex-col gap-1">
                {showGroup ? (
                  <p className={cx('px-3 pt-3 pb-1 text-micro font-medium tracking-wide text-ink-400 uppercase', index === 0 && 'pt-0')}>
                    {item.group}
                  </p>
                ) : null}
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cx(
                      'flex items-center gap-3 rounded-[var(--radius-md)] px-3 py-2 text-body-s transition-ui',
                      isActive ? 'bg-white/10 font-medium text-white' : 'text-ink-200 hover:bg-white/5 hover:text-white',
                    )
                  }
                >
                  <Icon name={item.icon} size={17} />
                  {item.label}
                </NavLink>
              </div>
            );
          })}
        </nav>

        <div className="min-w-0 flex-1">
          {/* Navigație orizontală pe ecrane mici */}
          <div className="-mx-4 mb-6 flex gap-2 overflow-x-auto px-4 lg:hidden">
            {ADMIN_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cx(
                    'whitespace-nowrap rounded-full border px-3 py-1.5 text-caption transition-ui',
                    isActive ? 'border-brand-500 bg-brand-600 text-white' : 'border-white/15 text-ink-200 hover:border-white/30',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>

          <Outlet />
        </div>
      </div>
    </AdminShell>
  );
}

/* ─────────────────────────── privire generală ─────────────────────────── */

interface Overview {
  accounts: number;
  suspended: number;
  restaurants: number;
  locations: number;
  users: number;
  displays: number;
  qr_codes: number;
  menus: number;
  mrr_cents: number;
  signups_7d: number;
}

export function AdminOverviewPage() {
  const session = useSession();
  const resource = useResource<Overview>(() => api.get<Overview>('/admin/overview'));
  const overview = resource.data;

  return (
    <div className="page-enter">
      <AdminHeader
        title="Privire generală"
        subtitle={`Platformă administrată de ${session.user?.email ?? '—'} · date live din baza de date`}
      />

      {resource.error ? (
        <InlineAlert tone="danger" title="Datele nu au putut fi încărcate">
          {resource.error}
        </InlineAlert>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <DarkKpi label="Conturi" value={formatNumber(overview?.accounts ?? 0)} hint={`${overview?.suspended ?? 0} suspendate`} />
        <DarkKpi label="Restaurante" value={formatNumber(overview?.restaurants ?? 0)} hint={`${overview?.locations ?? 0} locații`} />
        <DarkKpi label="Utilizatori" value={formatNumber(overview?.users ?? 0)} hint={`${overview?.signups_7d ?? 0} noi în 7 zile`} />
        <DarkKpi
          label="MRR"
          value={formatCurrency(overview?.mrr_cents ?? 0, 'RON')}
          hint="abonamente active"
          tone="brand"
        />
        <DarkKpi label="Meniuri" value={formatNumber(overview?.menus ?? 0)} hint="publicate sau în lucru" />
        <DarkKpi label="Coduri QR" value={formatNumber(overview?.qr_codes ?? 0)} hint="generate de restaurante" />
        <DarkKpi label="Ecrane conectate" value={formatNumber(overview?.displays ?? 0)} hint="Digital Display" />
        <DarkKpi
          label="Rată suspendare"
          value={overview && overview.accounts > 0 ? `${Math.round((overview.suspended / overview.accounts) * 100)}%` : '0%'}
          hint="din conturile existente"
        />
      </div>

      <div className="mt-8 grid gap-5 lg:grid-cols-3">
        <AdminCard className="lg:col-span-2">
          <h2 className="mb-4 text-body-l font-medium text-white">Sănătatea platformei</h2>
          <ul className="flex flex-col gap-3">
            {[
              ['API', 'Răspunde la /api/v1/health; erorile 5xx sunt înregistrate automat în system_events.'],
              ['Izolare tenant', 'Fiecare cerere rulează cu context de cont; accesul la datele altui cont întoarce 404, nu 403.'],
              ['Rollup & retenție', 'Agregările zilnice și curățarea datelor vechi rulează în worker, nu în cererea web.'],
              ['Audit', 'Fiecare acțiune sensibilă (suspendare, schimbare de plan, forțare logout) este jurnalizată.'],
            ].map(([title, text]) => (
              <li key={title} className="flex gap-3 border-b border-white/5 pb-3 last:border-0 last:pb-0">
                <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />
                <div className="flex flex-col">
                  <span className="text-body-s font-medium text-white">{title}</span>
                  <span className="text-caption text-ink-400">{text}</span>
                </div>
              </li>
            ))}
          </ul>
        </AdminCard>

        <AdminCard>
          <h2 className="mb-4 text-body-l font-medium text-white">Scurtături</h2>
          <div className="flex flex-col gap-2">
            {[
              ['/admin/accounts', 'Vezi și suspendă conturi'],
              ['/admin/plans', 'Modifică prețuri și limite'],
              ['/admin/flags', 'Activează module pe planuri'],
              ['/api/v1/health', 'Verifică starea API-ului'],
            ].map(([href, label]) => (
              <a
                key={href}
                href={href}
                className="flex items-center justify-between rounded-[var(--radius-md)] border border-white/10 px-3 py-2 text-body-s text-ink-200 transition-ui hover:border-white/25 hover:text-white"
              >
                {label}
                <Icon name="chevron-right" size={16} />
              </a>
            ))}
          </div>
        </AdminCard>
      </div>
    </div>
  );
}

/* ─────────────────────────────── conturi ─────────────────────────────── */

interface AdminAccount {
  id: string;
  public_id: string;
  name: string;
  slug: string;
  status: string;
  created_at: string;
  billing_email: string;
  restaurants: number;
  members: number;
  plan_code: string | null;
  plan_name: string | null;
  subscription_status: string | null;
  trial_ends_at: string | null;
}

export function AdminAccountsPage() {
  const toast = useToast();
  const resource = useResource<AdminAccount[]>(() => api.get<AdminAccount[]>('/admin/accounts'));
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState<AdminAccount | null>(null);
  const [busy, setBusy] = useState(false);

  const accounts = (resource.data ?? []).filter((account) =>
    `${account.name} ${account.slug} ${account.billing_email}`.toLowerCase().includes(query.toLowerCase()),
  );

  async function toggleStatus(account: AdminAccount) {
    setBusy(true);
    try {
      const action = account.status === 'suspended' ? 'reactivate' : 'suspend';
      await api.post(`/admin/accounts/${account.id}/${action}`);
      toast.success(
        action === 'suspend' ? `Contul „${account.name}” a fost suspendat` : `Contul „${account.name}” a fost reactivat`,
      );
      await resource.reload();
    } catch (error) {
      toast.error('Operațiunea a eșuat', errorMessage(error));
    } finally {
      setBusy(false);
      setPending(null);
    }
  }

  return (
    <div className="page-enter">
      <AdminHeader
        title="Conturi"
        subtitle="Fiecare cont este un tenant complet izolat: restaurante, locații, meniuri, utilizatori și abonament."
        actions={<AdminFilterInput value={query} onChange={setQuery} placeholder="Caută după nume, slug sau email…" />}
      />

      <AdminCard>
        {resource.loading ? (
          <AdminTableSkeleton />
        ) : resource.error ? (
          <InlineAlert tone="danger" title="Conturile nu au putut fi încărcate">
            {resource.error}
          </InlineAlert>
        ) : !accounts.length ? (
          <EmptyState
            title="Niciun cont găsit"
            description={query ? 'Încearcă alt termen de căutare.' : 'Conturile apar aici imediat ce un restaurant se înregistrează.'}
          />
        ) : (
          <AdminTable headers={['Cont', 'Plan', 'Resurse', 'Creat', 'Status', '']}>
            {accounts.map((account) => (
              <AdminRow key={account.id} tone={account.status === 'suspended' ? 'danger' : undefined}>
                <Td>
                  <div className="flex flex-col">
                    <span className="font-medium text-white">{account.name}</span>
                    <span className="font-mono text-caption text-ink-400">
                      {account.slug} · {account.billing_email}
                    </span>
                  </div>
                </Td>
                <Td>
                  <div className="flex flex-col gap-1">
                    <Badge tone={account.plan_code === 'pro' || account.plan_code === 'business' ? 'brand' : 'neutral'}>
                      {account.plan_name ?? 'fără plan'}
                    </Badge>
                    <span className="text-caption text-ink-400">
                      {account.subscription_status ?? '—'}
                      {account.trial_ends_at ? ` · trial până ${new Date(account.trial_ends_at).toLocaleDateString('ro-RO')}` : ''}
                    </span>
                  </div>
                </Td>
                <Td>
                  <span className="text-caption text-ink-400 nums">
                    {account.restaurants} restaurante · {account.members} membri
                  </span>
                </Td>
                <Td>
                  <span className="text-caption text-ink-400">{formatDateTime(account.created_at)}</span>
                </Td>
                <Td>
                  <span className="flex items-center gap-2">
                    <StatusDot tone={account.status === 'suspended' ? 'danger' : 'success'} />
                    {account.status === 'suspended' ? 'suspendat' : 'activ'}
                  </span>
                </Td>
                <Td className="text-right">
                  <Button
                    variant="secondary"
                    size="sm"
                    className={cx(
                      'border-white/15 bg-white/5 text-white',
                      account.status === 'suspended' ? 'hover:bg-brand-600/30' : 'hover:bg-danger/20',
                    )}
                    onClick={() => setPending(account)}
                  >
                    {account.status === 'suspended' ? 'Reactivează' : 'Suspendă'}
                  </Button>
                </Td>
              </AdminRow>
            ))}
          </AdminTable>
        )}
      </AdminCard>

      <ConfirmDialog
        open={Boolean(pending)}
        title={pending?.status === 'suspended' ? 'Reactivezi contul?' : 'Suspendzi contul?'}
        description={
          pending?.status === 'suspended'
            ? `Contul „${pending?.name}” va putea accesa din nou dashboard-ul, iar meniurile publice redevin disponibile.`
            : `Contul „${pending?.name}” își pierde accesul la dashboard, iar meniurile publice devin indisponibile. Datele nu se șterg.`
        }
        confirmLabel={pending?.status === 'suspended' ? 'Reactivează' : 'Suspendă'}
        tone={pending?.status === 'suspended' ? 'primary' : 'danger'}
        loading={busy}
        onClose={() => setPending(null)}
        onConfirm={() => void (pending && toggleStatus(pending))}
      />
    </div>
  );
}

/* ───────────────────────────── restaurante ───────────────────────────── */

interface AdminRestaurant {
  id: string;
  public_id: string;
  name: string;
  slug: string;
  is_demo: boolean;
  created_at: string;
  account_id: string;
  account_name: string;
  locations: number;
  menus: number;
}

export function AdminRestaurantsPage() {
  const session = useSession();
  const navigate = useNavigate();
  const [entering, setEntering] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<AdminRestaurant[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const pageSize = 20;

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setLoading(true);
      const q = query.trim();
      api
        .getRaw<{ data: AdminRestaurant[]; meta?: { total: number } }>(
          `/admin/restaurants?limit=${pageSize}&offset=${offset}${q ? `&q=${encodeURIComponent(q)}` : ''}`,
        )
        .then((response) => {
          setRows(response.data ?? []);
          setTotal(response.meta?.total ?? response.data?.length ?? 0);
          setLoadError(null);
        })
        .catch((err) => setLoadError(errorMessage(err)))
        .finally(() => setLoading(false));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [query, offset]);

  async function manage(restaurant: AdminRestaurant) {
    setEntering(restaurant.account_id);
    try {
      sessionStorage.setItem(
        'menuvia-admin-operate-account',
        JSON.stringify({
          id: restaurant.id,
          name: restaurant.name,
          account_id: restaurant.account_id,
          account_name: restaurant.account_name,
        }),
      );
      await session.switchAccount(restaurant.account_id);
      navigate('/admin/operate/menus');
    } finally {
      setEntering(null);
    }
  }

  return (
    <div className="page-enter">
      <AdminHeader
        title="Restaurante"
        subtitle="Caută după nume, slug sau cont. Se afișează 20 de rezultate odată, nu toată lista."
      />

      <AdminCard>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setOffset(0);
            }}
            placeholder="Caută restaurant…"
            className="h-10 min-w-[240px] flex-1 rounded-[var(--radius-md)] border border-white/15 bg-ink-950 px-3 text-body-s text-white"
          />
          <span className="text-caption text-ink-400">{total} restaurante</span>
        </div>
        {loading ? (
          <AdminTableSkeleton rows={4} />
        ) : loadError ? (
          <InlineAlert tone="danger" title="Lista nu a putut fi încărcată">
            {loadError}
          </InlineAlert>
        ) : !rows.length ? (
          <EmptyState title="Niciun restaurant" description="Schimbă căutarea sau așteaptă primele conturi." />
        ) : (
          <AdminTable headers={['Restaurant', 'Cont proprietar', 'Structură', 'Creat', 'Marcaje', '']}>
            {rows.map((restaurant) => (
              <AdminRow key={restaurant.id}>
                <Td>
                  <div className="flex flex-col">
                    <span className="font-medium text-white">{restaurant.name}</span>
                    <span className="font-mono text-caption text-ink-400">{restaurant.slug}</span>
                  </div>
                </Td>
                <Td>{restaurant.account_name}</Td>
                <Td>
                  <span className="text-caption text-ink-400 nums">
                    {restaurant.locations} locații · {restaurant.menus} meniuri
                  </span>
                </Td>
                <Td>
                  <span className="text-caption text-ink-400">{formatDateTime(restaurant.created_at)}</span>
                </Td>
                <Td>
                  {restaurant.is_demo ? <Badge tone="warning">demo</Badge> : <span className="text-caption text-ink-400">—</span>}
                </Td>
                <Td>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={entering === restaurant.account_id}
                    onClick={() => void manage(restaurant)}
                  >
                    Gestionează
                  </Button>
                </Td>
              </AdminRow>
            ))}
          </AdminTable>
        )}
        {total > pageSize ? (
          <div className="mt-4 flex items-center justify-between gap-3">
            <Button size="sm" variant="secondary" disabled={offset === 0} onClick={() => setOffset((value) => Math.max(0, value - pageSize))}>
              Înapoi
            </Button>
            <span className="text-caption text-ink-400">
              {offset + 1}–{Math.min(offset + pageSize, total)} din {total}
            </span>
            <Button size="sm" variant="secondary" disabled={offset + pageSize >= total} onClick={() => setOffset((value) => value + pageSize)}>
              Înainte
            </Button>
          </div>
        ) : null}
      </AdminCard>
    </div>
  );
}

/* ────────────────────────────── utilizatori ────────────────────────────── */

interface AdminUser {
  id: string;
  public_id: string;
  name: string;
  email: string;
  email_verified_at: string | null;
  is_platform_admin: boolean;
  last_login_at: string | null;
  created_at: string;
  status: string;
  memberships: number;
}

export function AdminUsersPage() {
  const toast = useToast();
  const resource = useResource<AdminUser[]>(() => api.get<AdminUser[]>('/admin/users'));
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState<AdminUser | null>(null);
  const [busy, setBusy] = useState(false);

  const users = (resource.data ?? []).filter((user) =>
    `${user.name} ${user.email}`.toLowerCase().includes(query.toLowerCase()),
  );

  async function forceLogout(user: AdminUser) {
    setBusy(true);
    try {
      const result = await api.post<{ revoked: number }>(`/admin/users/${user.id}/force-logout`);
      toast.success(
        result.revoked > 0
          ? `${result.revoked} sesiuni închise pentru ${user.email}`
          : `${user.email} nu avea sesiuni active`,
      );
      await resource.reload();
    } catch (error) {
      toast.error('Operațiunea a eșuat', errorMessage(error));
    } finally {
      setBusy(false);
      setPending(null);
    }
  }

  return (
    <div className="page-enter">
      <AdminHeader
        title="Utilizatori"
        subtitle="Toți utilizatorii platformei, indiferent de cont. Închiderea forțată a sesiunilor este auditată."
        actions={<AdminFilterInput value={query} onChange={setQuery} placeholder="Caută după nume sau email…" />}
      />

      <AdminCard>
        {resource.loading ? (
          <AdminTableSkeleton />
        ) : resource.error ? (
          <InlineAlert tone="danger" title="Utilizatorii nu au putut fi încărcați">
            {resource.error}
          </InlineAlert>
        ) : !users.length ? (
          <EmptyState title="Niciun utilizator găsit" />
        ) : (
          <AdminTable headers={['Utilizator', 'Aparținere', 'Ultima autentificare', 'Status', '']}>
            {users.map((user) => (
              <AdminRow key={user.id}>
                <Td>
                  <div className="flex items-center gap-3">
                    <Avatar name={user.name} size={32} />
                    <div className="flex flex-col">
                      <span className="flex items-center gap-2 font-medium text-white">
                        {user.name}
                        {user.is_platform_admin ? <Badge tone="brand">platform admin</Badge> : null}
                      </span>
                      <span className="font-mono text-caption text-ink-400">{user.email}</span>
                    </div>
                  </div>
                </Td>
                <Td>
                  <span className="text-caption text-ink-400 nums">{user.memberships} conturi</span>
                </Td>
                <Td>
                  <span className="text-caption text-ink-400">
                    {user.last_login_at ? relativeTime(user.last_login_at) : 'niciodată'}
                  </span>
                </Td>
                <Td>
                  <span className="flex items-center gap-2">
                    <StatusDot tone={user.status === 'active' ? 'success' : 'warning'} />
                    <span className="text-caption text-ink-400">
                      {user.status === 'active' ? (user.email_verified_at ? 'verificat' : 'neverificat') : user.status}
                    </span>
                  </span>
                </Td>
                <Td className="text-right">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="border-white/15 bg-white/5 text-white hover:bg-danger/20"
                    onClick={() => setPending(user)}
                  >
                    Închide sesiunile
                  </Button>
                </Td>
              </AdminRow>
            ))}
          </AdminTable>
        )}
      </AdminCard>

      <ConfirmDialog
        open={Boolean(pending)}
        title="Închizi toate sesiunile?"
        description={`Utilizatorul ${pending?.email ?? ''} va fi delogat de pe toate dispozitivele și va trebui să se autentifice din nou.`}
        confirmLabel="Închide sesiunile"
        tone="danger"
        loading={busy}
        onClose={() => setPending(null)}
        onConfirm={() => void (pending && forceLogout(pending))}
      />
    </div>
  );
}

/* ─────────────────────────── rutele consolei ─────────────────────────── */

export function AdminRoutes() {
  return (
    <Routes>
      <Route element={<AdminLayout />}>
        <Route index element={<AdminOverviewPage />} />
        <Route path="accounts" element={<AdminAccountsPage />} />
        <Route path="restaurants" element={<AdminRestaurantsPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="plans" element={<AdminPlans />} />
        <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
        <Route path="invoices" element={<AdminInvoicesPage />} />
        <Route path="flags" element={<AdminFlags />} />
        <Route path="audit" element={<AdminAudit />} />
        <Route path="system" element={<AdminSystemEvents />} />
        <Route path="operate" element={<Navigate to="/admin/operate/menus" replace />} />
        <Route path="operate/:section" element={<AdminOperate />} />
        <Route path="*" element={<AdminOverviewPage />} />
      </Route>
    </Routes>
  );
}
